import React from "react";
import { BiSearch, BiCodeAlt, BiRocket, BiLayout } from "@acme/ui";
import { FooterSite, HeaderSite, LandingFaq, LandingFeatures, LandingHero, LandingModelSupport } from "@acme/feature";
import Head from "next/head";

const GeminiApiUiPage = () => {
    const features = [
        {
            title: "Multimodal First",
            description: "Gemini was built to understand text, images, and code natively.",
            icon: <BiLayout className="text-3xl" />,
            colSpan: "md:col-span-2",
            bg: "bg-blue-50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-800/20"
        },
        {
            title: "Fast & Efficient",
            description: "Experience the speed of Gemini Flash and 1.5 Pro.",
            icon: <BiRocket className="text-3xl" />,
            colSpan: "md:col-span-1",
            bg: "bg-cyan-50 dark:bg-cyan-900/10 border-cyan-100 dark:border-cyan-800/20"
        },
        {
            title: "Large Context",
            description: "Handle massive amounts of information with Gemini 1.5's context window.",
            icon: <BiSearch className="text-3xl" />,
            colSpan: "md:col-span-1",
            bg: "bg-teal-50 dark:bg-teal-900/10 border-teal-100 dark:border-teal-800/20"
        },
        {
            title: "Code Integration",
            description: "Excellent coding capabilities for debugging and generation.",
            icon: <BiCodeAlt className="text-3xl" />,
            colSpan: "md:col-span-2",
            bg: "bg-indigo-50 dark:bg-indigo-900/10 border-indigo-100 dark:border-indigo-800/20"
        },
    ];

    const faqList = [
        { "question": "Are Gemini models free?", "answer": "Google provides a generous free tier for Gemini API usage, which you can use directly in DapperGPT." },
        { "question": "Does this support Gemini 1.5 Pro?", "answer": "Yes, we support the latest versions including Gemini 1.5 Pro and Flash." },
        { "question": "Can I switch between Gemini and other providers?", "answer": "Yes, DapperGPT lets you switch models and providers quickly in chat settings." },
        { "question": "Does Gemini support images and documents?", "answer": "Yes. Gemini is designed for multimodal work. You can use images and documents depending on model capabilities." },
        { "question": "Is this a good Gemini API playground alternative?", "answer": "Yes. It\u2019s a cleaner UI with folders, search, and saved prompts for repeated workflows." }
    ]

    return (
        <>
            <Head>
                <title>UI for Google Gemini API - DapperGPT</title>
                <meta name="description" content="A modern UI for Google Gemini API. Connect your API key, organize chats, and use Gemini models in a fast, beautiful multi-model workspace." />
                <meta property="og:title" content="UI for Google Gemini API" />
                <meta property="og:description" content="A professional interface for Gemini Pro & Ultra." />
            </Head>
            <div className="bg-white dark:bg-gray-900 min-h-screen flex flex-col font-sans text-gray-900 dark:text-gray-100 selection:bg-blue-500/30 transition-colors duration-300">
                <HeaderSite containerClassName="!max-w-7xl px-6" />

                <main className="flex-grow">
                    <LandingHero
                        title="UI for Google Gemini"
                        subtitle="Harness the multimodal capabilities of Gemini Pro and Ultra. Drag and drop images, documents, and video in a unified workspace."
                        badge="Gemini Interface"
                        heroImage="/page/gemini-api-ui.png"
                        color="blue"
                    />
                    <LandingFeatures
                        title="Gemini + DapperGPT"
                        subtitle="The perfect home for Google\u2019s latest AI models."
                        features={features}
                    />
                    <LandingModelSupport title="Supported Google Models">
                        <div className="flex items-center gap-2"><span className="text-2xl font-bold font-sans text-gray-900 dark:text-gray-100">Gemini Pro</span></div>
                        <div className="flex items-center gap-2"><span className="text-2xl font-bold text-gray-900 dark:text-gray-100">Gemini Ultra</span></div>
                        <div className="flex items-center gap-2"><span className="text-2xl font-bold font-serif text-gray-900 dark:text-gray-100">Gemini Flash</span></div>
                    </LandingModelSupport>
                    <LandingFaq items={faqList} />
                </main>

                <FooterSite />
            </div>
        </>
    );
};

export default GeminiApiUiPage;
